import * as d3 from 'd3';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as uuid from 'uuid';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { BASE_COLORS } from '@lib/ui/theme';
import { SnCamera } from 'libs/api/entities/src/lib/camera';
import { AddDateTimeFieldDialogComponent } from 'libs/form/src/lib/components/add-date-time-field-dialog/add-date-time-field-dialog.component';
import { SnFormFieldConfig } from 'libs/form/src/lib/types/form-field-config.type';
import { SnPlayerState } from 'libs/player/common/src/lib/enums';

import { CameraDetectionsFieldsGeneratorService } from '../../services/camera-detections-fields-generator.service';
import { CameraDetectionsService } from '../../services/camera-detections.service';

export class FsShape {
  public id: string;
  public points: any[];
  public gridPoints: any[];

  constructor() {
    this.points = [];
    this.gridPoints = [];
  }
}

@Component({
  selector: 'sn-fusus-camera-detections-dialog',
  templateUrl: './fusus-camera-detections-dialog.component.html',
  styleUrls: ['./fusus-camera-detections-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FususCameraDetectionsDialogComponent implements AfterViewInit, OnDestroy, OnInit {
  public zoneDrawn: boolean = false;
  public readonly form: UntypedFormGroup = new UntypedFormGroup({});
  public readonly formGenerated: UntypedFormGroup = new UntypedFormGroup({});

  public isPolygonComplete: boolean = false;
  public isLoading: boolean = false;
  public resolutionX: number;
  public resolutionY: number;
  public gridX: number;
  public gridY: number;
  public drawing: FsShape;
  public currentRoi = null;
  public svgWidth: number = 734; // 734
  public svgHeight: number = 489; // 489

  public previousPoints: object[] = [];
  public currentRule: string;

  public timeAndDay: object[] = [];
  public selectedSchedule: string = 'repeated_schedule';

  public formFields: SnFormFieldConfig[] = [
    {
      templateOptions: {
        label: 'Camera Rules',
      },
      wrappers: ['form-field-group'],
      fieldGroupClassName: 'fusus-camera-detections-field-row',
      fieldGroup: [
        {
          key: 'ruleType',
          type: 'select',
          className: 'fusus-camera-detections-field-col-12',
          defaultValue: this.data.camera.supportedAiDetections.sort()[1].type,
          templateOptions: {
            label: 'Rule Type',
            options: this.data.camera.supportedAiDetections.map((res: any) => {
              return {
                label: res.name,
                value: res.type,
              };
            }),
            change: () => {
              this.reset();
              this.currentRule = this.formModel.ruleType;
              if (this.formModel.ruleType === 'vca_line_crossing_detection') {
                this.addLineCrossingLine();
              }
              if (this.formModel.ruleType === 'vca_smart_motion_detection') {
                this.addSmartMotionDetection();
              }
            },
          },
        },
      ],
    },
    {
      templateOptions: {
        label: 'Schedule',
      },
      hideExpression: () => !this.zoneDrawn || this.currentRule === 'vca_smart_motion_detection',

      wrappers: ['form-field-group'],
      fieldGroupClassName: 'fusus-camera-detections-field-row',
      fieldGroup: [
        {
          key: 'schedule',
          type: 'radio',
          className: 'fusus-camera-detections-field-col-12',
          defaultValue: 'repeated_schedule',
          templateOptions: {
            label: 'Schedule',
            options: [
              { label: 'Repeated Schedule', value: 'repeated_schedule' },
              { label: 'Always', value: 'always' },
            ],
            change: (event, field) => {
              if (field.value === 'always') {
                this.selectedSchedule = 'always';
                this.timeAndDay = [];
              }
              if (field.value === 'repeated_schedule') {
                this.selectedSchedule = 'repeated_schedule';
                this.timeAndDay = [];
              }
            },
          },
        },
      ],
    },
  ];

  public formFieldsGenerated: SnFormFieldConfig[] = [];

  public formModel: any = {};
  public formModelGenerated: any = {};

  private _shapes = [];
  private _dragging: FsShape;
  private _startPoint: any;
  private _svg: any;
  private _svgElement;
  private _polygon: FsShape;

  private readonly _playerState$ = new Subject<SnPlayerState>();
  private readonly _destroy$ = new Subject<void>();

  get playerState$(): Observable<SnPlayerState> {
    return this._playerState$.asObservable();
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      camera: SnCamera;
      detections: any;
    },
    private readonly _dialogRef: MatDialogRef<FususCameraDetectionsDialogComponent>,
    private readonly _elementRef: ElementRef,
    private readonly _cd: ChangeDetectorRef,
    private readonly _matDialog: MatDialog,
    private readonly _cameraDetectionsFieldsGenerator: CameraDetectionsFieldsGeneratorService,
    private readonly _cameraDetections: CameraDetectionsService,
  ) {}

  public ngOnInit(): void {}

  public ngAfterViewInit(): void {
    this._svg = d3.select('#aiGridCanvas');
    this._svgElement = this._elementRef.nativeElement.querySelector('.coveringCanvas');
    this._playerState$.pipe(takeUntil(this._destroy$)).subscribe((state) => {
      if (this.currentRoi) {
        return;
      }

      if (state === SnPlayerState.Playing) {
        setTimeout(() => {
          this._genGridResolution();
          this.updateGridResolution();
          this._loadData();
        }, 500);
      }

      if (state === SnPlayerState.Playing) {
        setTimeout(() => {
          this._loadData();
        }, 750);
      }
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._svg.remove();
  }

  @HostListener('window:resize', ['$event'])
  public onResize(): void {
    if (this._svg) {
      this.updateGridResolution();
    }
  }

  public updateGridResolution(): void {
    const svgBox = this._svgElement.getBBox();
    this.resolutionX = svgBox.width;
    this.resolutionY = svgBox.height;
    this.gridX = (svgBox.width - 1) / 160;
    this.gridY = (svgBox.height - 1) / 90;
    this._cd.detectChanges();
  }

  public onStateChange(state: SnPlayerState): void {
    this._playerState$.next(state);
  }

  public handleMouseMove(event: any): void {
    if (!this.drawing) {
      return;
    }
    if (event.target.id === 'gridRect' || event.target.hasAttribute('is-handle')) {
      const g = this._svg.select('#' + this.drawing.id);
      g.select('line').remove();
      g.insert('line', ':first-child')
        .attr('x1', this._startPoint[0])
        .attr('y1', this._startPoint[1])
        .attr('x2', event.offsetX)
        .attr('y2', event.offsetY)
        .attr('stroke', BASE_COLORS['accent-400'])
        .attr('stroke-width', 2);

      const x = event.offsetX;
      const y = event.offsetY;
      const gridX = this._round(Math.max(5, Math.min(this.resolutionX - 5, x)), this.gridX);
      const gridY = this._round(Math.max(5, Math.min(this.resolutionY - 5, y)), this.gridY);
      this.drawing.points.splice(this.drawing.points.length - 1, 1, [gridX, gridY]);
      this._updateFsShape(this.drawing);
    }
  }

  public handleMouseUp(event: any): void {
    if (this._dragging || this.zoneDrawn) {
      return;
    }
    if (event.target.hasAttribute('is-handle')) {
      this._closePolygon(this.drawing);
      return;
    }
    const x = event.offsetX;
    const y = event.offsetY;
    const gridX = this._round(Math.max(5, Math.min(this.resolutionX - 5, x)), this.gridX);
    const gridY = this._round(Math.max(5, Math.min(this.resolutionY - 5, y)), this.gridY);
    this._startPoint = [gridX, gridY];

    if (!this.drawing) {
      const shape = new FsShape();
      shape.id = `shape_${uuid.v4()}`;
      this._shapes.push(shape);
      this.drawing = shape;
      this._svg.append('g').attr('id', shape.id);
      this.drawing.points.push(this._startPoint);
    }

    const g = this._svg.select('#' + this.drawing.id);
    this.drawing.points.push(this._startPoint);
    g.select('polyline').remove();
    g.append('polyline')
      .attr('points', this.drawing.points)
      .style('fill', 'none')
      .attr('stroke', BASE_COLORS['accent-400'])
      .attr('stroke-width', 2);
    this._updateFsShape(this.drawing);
  }

  public reset(): void {
    const polygon = this._svg.selectAll('g');
    polygon.remove();
    this._dragging = null;
    this.drawing = null;
    this.zoneDrawn = false;
    this.timeAndDay = [];
    this.formGenerated.reset();
  }

  public submit(): void {
    this.isLoading = true;
    const gridPoints = [];
    const x = 9999 / this.svgWidth;
    const y = 9999 / this.svgHeight;
    if (this.selectedSchedule === 'always') {
      this.timeAndDay = [
        {
          day: 'Sun',
          start: '00:00',
          end: '23:59',
        },
        {
          day: 'Mon',
          start: '00:00',
          end: '23:59',
        },
        {
          day: 'Tue',
          start: '00:00',
          end: '23:59',
        },
        {
          day: 'Wed',
          start: '00:00',
          end: '23:59',
        },
        {
          day: 'Thu',
          start: '00:00',
          end: '23:59',
        },
        {
          day: 'Fri',
          start: '00:00',
          end: '23:59',
        },
        {
          day: 'Sat',
          start: '00:00',
          end: '23:59',
        },
      ];
    }
    this._polygon.points.forEach((arry) => {
      gridPoints.push({ x: Math.floor(arry[0] * x), y: Math.floor(arry[1] * y) });
    });

    const detectionsData = {
      name: this.formModel,
      data: this.formModelGenerated,
      schedule: this.timeAndDay,
      gridPoints,
    };
    this._cameraDetections.addDetection(detectionsData, this.data.camera).subscribe((res) => {
      this._dialogRef.close(res);
    });
  }

  public addTimeAndDay(): void {
    this._matDialog
      .open(AddDateTimeFieldDialogComponent)
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        if (result) {
          this.timeAndDay.push(result);
          this._cd.detectChanges();
        }
        return;
      });
  }

  public editTimeAndDay(item: any, i?: number): void {
    this._matDialog
      .open(AddDateTimeFieldDialogComponent, {
        data: item,
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        if (result) {
          this.timeAndDay.splice(i, 1);
          this.timeAndDay.push(result);
          this._cd.detectChanges();
        }
        return;
      });
  }

  public deleteTimeAndDay(i: number): void {
    this.timeAndDay.splice(i, 1);
  }

  public cancel(): void {
    this._dialogRef.close();
  }

  public addLineCrossingLine(): void {
    const shape = new FsShape();
    shape.id = `shape_${uuid.v4()}`;
    this._shapes.push(shape);
    this.drawing = shape;
    this._svg.append('g').attr('id', shape.id);

    const g = this._svg.select('#' + this.drawing.id);

    g.append('circle')
      .attr('cx', 100)
      .attr('cy', 100)
      .attr('r', 5)
      .attr('fill', BASE_COLORS['accent-400'])
      .attr('fill-opacity', '0.9')
      .attr('stroke', '#000')
      .attr('stroke-width', '2');
    g.append('circle')
      .attr('cx', 200)
      .attr('cy', 200)
      .attr('r', 5)
      .attr('fill', BASE_COLORS['accent-400'])
      .attr('fill-opacity', '0.9')
      .attr('stroke', '#000')
      .attr('stroke-width', '2');
    g.append('circle')
      .attr('cx', 300)
      .attr('cy', 300)
      .attr('r', 5)
      .attr('fill', BASE_COLORS['accent-400'])
      .attr('fill-opacity', '0.9')
      .attr('stroke', '#000')
      .attr('stroke-width', '2');

    this.drawing.points = [
      [100, 100],
      [200, 200],
      [300, 300],
    ];

    g.append('polyline')
      .attr('points', shape.points)
      .attr('stroke', BASE_COLORS['accent-400'])
      .attr('stroke-width', 2)
      .attr('fill', BASE_COLORS['accent-400'])
      .attr('fill-opacity', '0.25');

    this._updateFsShape(shape, true);
    this._polygon = this.drawing;
    this.drawing = null;
    this.zoneDrawn = true;
    this._genDetectionForm(this.formModel.ruleType);
  }

  public addSmartMotionDetection(): void {
    const shape = new FsShape();
    shape.id = `shape_${uuid.v4()}`;
    this._shapes.push(shape);
    this.drawing = shape;
    this._svg.append('g').attr('id', shape.id);

    const g = this._svg.select('#' + this.drawing.id);

    g.append('circle')
      .attr('cx', 100)
      .attr('cy', 100)
      .attr('r', 5)
      .attr('fill', BASE_COLORS['accent-400'])
      .attr('fill-opacity', '0.9')
      .attr('stroke', '#000')
      .attr('stroke-width', '2');
    g.append('circle')
      .attr('cx', 100)
      .attr('cy', 200)
      .attr('r', 5)
      .attr('fill', BASE_COLORS['accent-400'])
      .attr('fill-opacity', '0.9')
      .attr('stroke', '#000')
      .attr('stroke-width', '2');
    g.append('circle')
      .attr('cx', 200)
      .attr('cy', 200)
      .attr('r', 5)
      .attr('fill', BASE_COLORS['accent-400'])
      .attr('fill-opacity', '0.9')
      .attr('stroke', '#000')
      .attr('stroke-width', '2');
    g.append('circle')
      .attr('cx', 200)
      .attr('cy', 100)
      .attr('r', 5)
      .attr('fill', BASE_COLORS['accent-400'])
      .attr('fill-opacity', '0.9')
      .attr('stroke', '#000')
      .attr('stroke-width', '2');

    this.drawing.points = [
      [100, 100],
      [100, 100],
      [100, 200],
      [200, 200],
      [200, 100],
    ];

    this._closePolygon(shape);
  }
  private _genGridResolution(): void {
    const element = this._elementRef.nativeElement;
    const video: HTMLVideoElement[] = element.querySelectorAll('video');
    const topVideo = video[video.length - 1];
    const videoRatio = topVideo.videoHeight / topVideo.videoWidth;
    const clientRatio = topVideo.clientHeight / topVideo.clientWidth;
    if (videoRatio > clientRatio) {
      const ratio = topVideo.clientHeight / topVideo.videoHeight;
      const newVideoWidth = topVideo.videoWidth * ratio;
      this.svgWidth = newVideoWidth;
      this.svgHeight = topVideo.clientHeight;
      this._svgElement.style.left = `calc(46% - ${newVideoWidth / 2}px)`;
      this._svgElement.style.top = '0px';
      this._cd.detectChanges();
    } else {
      const ratio = topVideo.clientWidth / topVideo.videoWidth;
      const newVideoHeight = topVideo.videoHeight * ratio;
      if (!isNaN(newVideoHeight)) {
        this.svgHeight = newVideoHeight;
        this.svgWidth = topVideo.clientWidth;
        this._svgElement.style.top = `calc(50% - ${newVideoHeight / 2}px)`;
        this._svgElement.style.left = '0px';
        this._cd.detectChanges();
      }
    }
  }

  private _round(p: number, n: number): any {
    return p % n < n / 2 ? p - (p % n) : p + n - (p % n);
  }

  private _handleDrag(self: any): any {
    return function (d: any): void {
      const dragCircle = d3.select(this);
      const newPoints = [];
      let circle: d3.Selection<any, unknown, null, undefined>;
      const x = d3.event.x;
      const y = d3.event.y;
      const gridX = self._round(Math.max(5, Math.min(self.resolutionX - 5, x)), self.gridX);
      const gridY = self._round(Math.max(5, Math.min(self.resolutionY - 5, y)), self.gridY);

      const polyline: any = d3.select(this.parentNode).select('polyline');
      const poly: any = d3.select(this.parentNode).select('polygon');
      const circles: any = d3.select(this.parentNode).selectAll('circle');
      dragCircle.attr('cx', gridX).attr('cy', gridY);
      for (let i = 0; i < circles._groups[0].length; i++) {
        circle = d3.select(circles._groups[0][i]);
        newPoints.push([Number(circle.attr('cx')), Number(circle.attr('cy'))]);
      }
      poly.attr('points', newPoints);
      polyline.attr('points', newPoints);

      self._dragging = self._shapes.find((s: { id: any }) => s.id === this.parentNode.id);
      self._dragging.points = newPoints;
    };
  }

  private _endDrag(self: any): any {
    return (d: any) => {
      self._dragging = null;
    };
  }

  private _updateFsShape(shape: FsShape, closed: boolean = false): void {
    this._updatePoints(shape, closed);
  }

  private _updatePoints(shape: FsShape, closed: boolean = false): void {
    const g = this._svg.select('#' + shape.id);
    g.selectAll('circle').remove();
    let pointLength = shape.points.length - 1;
    if (closed) {
      pointLength++;
    }
    for (let i = 0; i < pointLength; i++) {
      const point = shape.points[i];
      const circle = g
        .append('circle')
        .attr('cx', point[0])
        .attr('cy', point[1])
        .attr('r', 5)
        .attr('fill', BASE_COLORS['accent-400'])
        .attr('fill-opacity', '0.9')
        .attr('stroke', '#000')
        .attr('stroke-width', '2');
      if (closed) {
        const dragger = d3.drag().on('drag', this._handleDrag(this)).on('end', this._endDrag(this));
        circle.call(dragger).style('cursor', 'move');
      } else {
        if (i === 0) {
          circle.attr('is-handle', 'true').style('cursor', 'pointer');
        }
      }
    }
  }

  private _closePolygon(shape: FsShape): void {
    const g = this._svg.select('#' + shape.id);
    g.insert('polygon', ':first-child')
      .attr('points', shape.points)
      .style('fill-opacity', '0.3')
      .style('fill', BASE_COLORS['accent-400'])
      .attr('stroke', BASE_COLORS['accent-400'])
      .attr('stroke-width', 2);

    g.select('polyline').remove();
    g.select('line').remove();

    this.drawing.points.splice(0, 1);
    this._updateFsShape(shape, true);
    this._polygon = this.drawing;
    this.drawing = null;
    this.zoneDrawn = true;
    this._genDetectionForm(this.formModel.ruleType);
  }

  private _genDetectionForm(ruleType: string): void {
    const data = this._cameraDetectionsFieldsGenerator.genFields(ruleType);
    this.formFieldsGenerated = data;
  }

  private _loadData(): void {
    if (this.data.detections?.extraParams?.field) {
      this.data.detections.extraParams.field.forEach((field) => {
        field.forEach((f) => {
          this.previousPoints.push(f);
        });
      });
    }

    if (this.data.detections.extraParams?.line) {
      this.data.detections.extraParams.line.forEach((line) => {
        line.forEach((l) => {
          this.previousPoints.push(l);
        });
      });
    }

    if (this.data.detections.extraParams?.scheduledRoi) {
      this.data.detections.extraParams.scheduledRoi[0].polygonStd.forEach((polygon) => {
        this.previousPoints.push(polygon);
      });
    }

    this._createLoadedFsShape(this.previousPoints);

    this._loadDataForDetection(this.data.detections.type);
    this._cd.detectChanges();
  }

  // TODO - Work In Progress
  private _createLoadedFsShape(points: any): void {
    const shape = new FsShape();
    shape.id = `shape_${uuid.v4()}`;
    this._shapes.push(shape);
    this.drawing = shape;
    this._svg.append('g').attr('id', shape.id);

    const g = this._svg.select('#' + this.drawing.id);

    points.forEach((point) => {
      const x = Math.floor(point.x / (9999 / this.svgWidth));
      const y = Math.floor(point.y / (9999 / this.svgHeight));
      g.append('circle')
        .attr('cx', x)
        .attr('cy', y)
        .attr('r', 5)
        .attr('fill', BASE_COLORS['accent-400'])
        .attr('fill-opacity', '0.9')
        .attr('stroke', '#000')
        .attr('stroke-width', '2');

      this.drawing.points.push([x, y]);
    });

    g.insert('polygon', ':first-child')
      .attr('points', shape.points)
      .style('fill-opacity', '0.3')
      .style('fill', BASE_COLORS['accent-400'])
      .attr('stroke', BASE_COLORS['accent-400'])
      .attr('stroke-width', 2);

    g.select('polyline').remove();
    g.select('line').remove();

    this._updateFsShape(shape, true);
    this._polygon = this.drawing;
    this.drawing = null;
    this.zoneDrawn = true;
    this._genDetectionForm(this.formModel.ruleType);
    this._cd.detectChanges();
  }

  private _loadDataForDetection(detection: string): void {
    if (detection !== 'vca_smart_motion_detection') {
      this.formModel = {
        ruleType: this.data.detections.type,
        schedule: 'repeated_schedule',
      };
      this.selectedSchedule = 'repeated_schedule';
      this.timeAndDay = this.data.detections.extraParams.schedule;
    }

    if (detection === 'vca_smart_motion_detection') {
      this.formModel = {
        ruleType: this.data.detections.type,
      };

      this.currentRule = 'vca_smart_motion_detection';
      this._genDetectionForm(detection);
    }

    switch (detection) {
      case 'vca_loitering_detection':
        this.formModelGenerated = {
          triggerName: this.data.detections.extraParams.ruleName,
          loiteringTriggerCondition: this.data.detections.extraParams.stayTime,
        };
        break;
      case 'vca_crowd_detection':
        this.formModelGenerated = {
          triggerName: this.data.detections.extraParams.ruleName,
          peopleNumber: this.data.detections.extraParams.peopleNumber,
          enteredZone: this.data.detections.extraParams.enterDelay,
          leftZone: this.data.detections.extraParams.leaveDelay,
        };
        break;
      case 'vca_smart_motion_detection':
        this.formModelGenerated = {
          smartMotionDetectionStart: this.data.detections.extraParams.schedule.time.begin,
          smartMotionDetectionEnd: this.data.detections.extraParams.schedule.time.end,
          peopleDetection: this.data.detections.extraParams.scheduledRoi[0].humanDetection.enable,
          vehicleDetection:
            this.data.detections.extraParams.scheduledRoi[0].vehicleDetection.enable,
          smartMotionActivity: this.data.detections.extraParams.scheduledRoi[0].itemSize,
          activityMergeInt:
            this.data.detections.extraParams.scheduledRoi[0].temporalFilter.activityMergeInterval,
          minActivityDur:
            this.data.detections.extraParams.scheduledRoi[0].temporalFilter.minActivityDuration,
        };
        break;
      case 'vca_line_crossing_detection':
        this.formModelGenerated = {
          triggerName: this.data.detections.extraParams.ruleName,
          lineCrossingTriggerCondition: this.data.detections.extraParams.direction,
        };
        break;
      case 'vca_intrusion_detection':
        this.formModelGenerated = {
          triggerName: this.data.detections.extraParams.ruleName,
          intrusionTriggerCondition: this.data.detections.extraParams.walkingDirection,
        };
        break;
      default:
        break;
    }
  }
}
